/**
 * Organization Dual-Write Test Suite
 *
 * This utility tests that organization settings updates
 * are properly synced between Firestore and Supabase.
 *
 * Usage:
 *   import { runOrganizationDualWriteTests } from './utils/organizationDualWriteTest';
 *   await runOrganizationDualWriteTests(db, options);
 */

import { sanitizeForFirestore } from '../global/Firebase/productSanitize'
import { updateOrganization } from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { organizationsApi } from '../global/API/typedApiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom } from '../global/AtomsProvider';

// Test result tracking
const testResults = {
  passed: [],
  failed: [],
  skipped: [],
};

/**
 * Test variation definitions
 */
export const ORGANIZATION_TEST_VARIATIONS = [
  // Module Settings
  {
    id: 'O1.1',
    name: 'Enable loyalty module',
    category: 'modules',
    type: 'update',
    field: 'moduleEnabled',
    value: { loyalty: true },
  },
  {
    id: 'O1.2',
    name: 'Disable loyalty module',
    category: 'modules',
    type: 'update',
    field: 'moduleEnabled',
    value: { loyalty: false },
  },
  {
    id: 'O1.3',
    name: 'Enable marketplace module',
    category: 'modules',
    type: 'update',
    field: 'moduleEnabled',
    value: { marketplace: true },
  },
  {
    id: 'O1.4',
    name: 'Disable marketplace module',
    category: 'modules',
    type: 'update',
    field: 'moduleEnabled',
    value: { marketplace: false },
  },
  {
    id: 'O1.5',
    name: 'Enable reports module',
    category: 'modules',
    type: 'update',
    field: 'moduleEnabled',
    value: { reports: true },
  },
  {
    id: 'O1.6',
    name: 'Enable multiple modules',
    category: 'modules',
    type: 'update',
    field: 'moduleEnabled',
    value: { loyalty: true, marketplace: true, reports: true },
  },

  // Marketplace Settings
  {
    id: 'O2.1',
    name: 'Set store for marketplace',
    category: 'marketplace',
    type: 'update',
    field: 'storeForMarketplace',
    useFirstStore: true,
  },
  {
    id: 'O2.2',
    name: 'Clear store for marketplace',
    category: 'marketplace',
    type: 'update',
    field: 'storeForMarketplace',
    value: null,
  },

  // Link Settings
  {
    id: 'O3.1',
    name: 'Enable link',
    category: 'link_settings',
    type: 'update',
    field: 'linkEnabled',
    value: true,
  },
  {
    id: 'O3.2',
    name: 'Disable link',
    category: 'link_settings',
    type: 'update',
    field: 'linkEnabled',
    value: false,
  },

  // Stock Settings
  {
    id: 'O4.1',
    name: 'Enable disable out-of-stock products',
    category: 'stock_settings',
    type: 'update',
    field: 'disableNotInStockProducts',
    value: true,
  },
  {
    id: 'O4.2',
    name: 'Disable out-of-stock product hiding',
    category: 'stock_settings',
    type: 'update',
    field: 'disableNotInStockProducts',
    value: false,
  },

  // Basic Info
  {
    id: 'O5.1',
    name: 'Update organization name',
    category: 'basic_info',
    type: 'update',
    field: 'name',
    generateValue: () => `Test Org ${Date.now()}`,
  },
  {
    id: 'O5.2',
    name: 'Update organization description',
    category: 'basic_info',
    type: 'update',
    field: 'description',
    generateValue: () => `Test description ${Date.now()}`,
  },
  {
    id: 'O5.3',
    name: 'Update organization phone',
    category: 'basic_info',
    type: 'update',
    field: 'phone',
    generateValue: () => `+1${Math.floor(Math.random() * 9000000000) + 1000000000}`,
  },
  {
    id: 'O5.4',
    name: 'Update organization address',
    category: 'basic_info',
    type: 'update',
    field: 'address',
    generateValue: () => `${Math.floor(Math.random() * 999) + 1} Test Street`,
  },

  // Currency Settings
  {
    id: 'O6.1',
    name: 'Update currency to USD',
    category: 'currency',
    type: 'update',
    field: 'currency',
    value: { code: 'USD', symbol: '$', name: 'US Dollar' },
  },
  {
    id: 'O6.2',
    name: 'Update currency to EUR',
    category: 'currency',
    type: 'update',
    field: 'currency',
    value: { code: 'EUR', symbol: '€', name: 'Euro' },
  },
];

/**
 * Wait for specified milliseconds
 */
const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Get current organization reference
 */
const getOrganizationRef = () => {
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);

  if (!organization?.id || !organization?.ref) {
    throw new Error('No organization found in state');
  }

  return organization.ref;
};

/**
 * Get first store ID for marketplace tests
 */
const getFirstStoreId = () => {
  const jotaiStore = getDefaultStore();
  const storesDict = jotaiStore.get(storesDictAtom);
  const storeIds = Object.keys(storesDict || {});
  return storeIds.length > 0 ? storeIds[0] : null;
};

/**
 * Compare organization data between Firestore and Supabase
 */
const compareOrganization = async (organizationId, options = {}) => {
  const { logSessionId } = options;

  try {
    const url = `/organizations/compare/${organizationId}${logSessionId ? `?logSessionId=${logSessionId}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Compare organization failed:', error);
    throw error;
  }
};

/**
 * Run a single organization test variation
 */
const runSingleTest = async (db, variation, options = {}) => {
  const { delayMs = 8000, logSessionId, originalValues = {} } = options;
  const testId = variation.id;
  const startTime = Date.now();

  console.log(`\n=== Running test ${testId}: ${variation.name} ===`);

  try {
    const organizationRef = getOrganizationRef();
    const organizationId = organizationRef.id;

    // Get current organization data from atom (needed for merging nested objects)
    const jotaiStore = getDefaultStore();
    const currentOrganization = jotaiStore.get(organizationAtom);

    // Determine the value to set
    let updateValue;
    if (variation.generateValue) {
      updateValue = variation.generateValue();
    } else if (variation.useFirstStore) {
      updateValue = getFirstStoreId();
      if (!updateValue) {
        console.log(`⚠️ Skipping test ${testId}: No stores available for marketplace test`);
        return { testId, status: 'skipped', reason: 'No stores available' };
      }
    } else {
      updateValue = variation.value;
    }

    // Store original value for restoration
    if (!originalValues[variation.field]) {
      try {
        const currentOrg = await organizationsApi.getOrganizationById(organizationId);
        const fieldMap = {
          moduleEnabled: 'module_enabled',
          storeForMarketplace: 'store_for_marketplace',
          linkEnabled: 'link_enabled',
          disableNotInStockProducts: 'disable_not_in_stock_products',
          name: 'name',
          description: 'description',
          phone: 'phone',
          address: 'address',
          currency: 'currency',
        };
        const supabaseField = fieldMap[variation.field] || variation.field;
        originalValues[variation.field] = currentOrg[supabaseField];
      } catch (e) {
        console.log(`Note: Could not get original value for ${variation.field}`);
      }
    }

    // For moduleEnabled, we need to merge with existing values (like the real app does)
    // Firestore .update() does shallow merge, so nested objects get replaced entirely
    let finalValue = updateValue;
    if (variation.field === 'moduleEnabled' && currentOrganization?.moduleEnabled) {
      // Merge the test value with existing moduleEnabled (like OrgDetailScreen does)
      finalValue = {
        ...currentOrganization.moduleEnabled,
        ...updateValue,
      };
      console.log(`Merging moduleEnabled: existing keys = ${Object.keys(currentOrganization.moduleEnabled).length}, adding = ${JSON.stringify(sanitizeForFirestore(updateValue))}`);
    }

    // Create update object
    const updateData = {
      [variation.field]: finalValue,
    };

    console.log(`Updating ${variation.field} to:`, JSON.stringify(sanitizeForFirestore(finalValue)));

    // Step 1: Update via Firestore (triggers dual-write)
    await updateOrganization(organizationRef, updateData);
    console.log(`✓ Firestore update complete`);

    // Step 2: Wait for async Supabase sync
    console.log(`Waiting ${delayMs}ms for sync...`);
    await wait(delayMs);

    // Step 3: Compare data
    console.log(`Comparing organization data...`);
    const comparison = await compareOrganization(organizationId, { logSessionId });

    // Map field names from camelCase to snake_case for comparison
    const fieldMap = {
      moduleEnabled: 'module_enabled',
      storeForMarketplace: 'store_for_marketplace',
      linkEnabled: 'link_enabled',
      disableNotInStockProducts: 'disable_not_in_stock_products',
      name: 'name',
      description: 'description',
      phone: 'phone',
      address: 'address',
      currency: 'currency',
    };
    const testedField = fieldMap[variation.field] || variation.field;

    // Check only the tested field, not all fields
    const testedFieldDiff = comparison.differences?.find(d => d.field === testedField);
    const otherDiffs = comparison.differences?.filter(d => d.field !== testedField) || [];

    if (!testedFieldDiff) {
      // The tested field matches - test passes
      console.log(`✅ Test ${testId} PASSED - ${testedField} matches`);
      if (otherDiffs.length > 0) {
        console.log(`⚠️ Note: ${otherDiffs.length} other field(s) differ (not relevant to this test)`);
      }
      testResults.passed.push({
        variation,
        duration: Date.now() - startTime,
      });
      return { testId, status: 'passed', comparison };
    } else {
      console.log(`❌ Test ${testId} FAILED - ${testedField} mismatch`);
      console.log('Tested field difference:', JSON.stringify(sanitizeForFirestore(testedFieldDiff), null, 2));
      testResults.failed.push({
        variation,
        duration: Date.now() - startTime,
        result: { differences: [testedFieldDiff] },
      });
      return { testId, status: 'failed', comparison };
    }
  } catch (error) {
    console.error(`❌ Test ${testId} ERROR:`, error.message);
    testResults.failed.push({
      variation,
      duration: Date.now() - startTime,
      error: error.message,
    });
    return { testId, status: 'error', error: error.message };
  }
};

/**
 * Run organization dual-write tests
 */
export const runOrganizationDualWriteTests = async (db, options = {}) => {
  const {
    variations = ORGANIZATION_TEST_VARIATIONS,
    cleanup = true,
    delayMs = 8000,
    logSessionId,
  } = options;

  console.log('\n========================================');
  console.log('  ORGANIZATION DUAL-WRITE TEST SUITE');
  console.log('========================================\n');

  // Reset results
  testResults.passed = [];
  testResults.failed = [];
  testResults.skipped = [];

  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);

  if (!organization) {
    throw new Error('No organization found. Please log in first.');
  }

  console.log(`Testing organization: ${organization.name} (${organization.id})`);
  console.log(`Running ${variations.length} test variations...\n`);

  // Store original values for cleanup
  const originalValues = {};

  for (const variation of variations) {
    await runSingleTest(db, variation, { delayMs, logSessionId, originalValues });
  }

  // Summary
  console.log('\n========================================');
  console.log('  TEST SUMMARY');
  console.log('========================================');
  console.log(`✅ Passed: ${testResults.passed.length}`);
  console.log(`❌ Failed: ${testResults.failed.length}`);
  console.log(`⚠️ Skipped: ${testResults.skipped.length}`);

  if (testResults.failed.length > 0) {
    console.log('\nFailed tests:');
    testResults.failed.forEach(t => {
      console.log(`  - ${t.testId}: ${t.name}`);
      if (t.error) console.log(`    Error: ${t.error}`);
      if (t.differences) {
        t.differences.forEach(d => {
          console.log(`    ${d.field}: Supabase=${JSON.stringify(sanitizeForFirestore(d.supabaseValue))}, Firestore=${JSON.stringify(sanitizeForFirestore(d.firestoreValue))}`);
        });
      }
    });
  }

  // Cleanup: Restore original values
  if (cleanup && Object.keys(originalValues).length > 0) {
    console.log('\nCleaning up - restoring original values...');
    try {
      const organizationRef = getOrganizationRef();
      await updateOrganization(organizationRef, originalValues);
      console.log('✓ Original values restored');
    } catch (e) {
      console.log('⚠️ Failed to restore original values:', e.message);
    }
  }

  return {
    passed: testResults.passed,
    failed: testResults.failed,
    skipped: testResults.skipped,
    total: variations.length,
    successRate: testResults.passed.length / (testResults.passed.length + testResults.failed.length) * 100,
  };
};

/**
 * Run a quick smoke test for organizations
 */
export const runQuickOrganizationSmokeTest = async (db, options = {}) => {
  const { logSessionId } = options;

  console.log('\n========================================');
  console.log('  ORGANIZATION QUICK SMOKE TEST');
  console.log('========================================\n');

  // Just test one moduleEnabled update
  const quickVariations = [
    ORGANIZATION_TEST_VARIATIONS.find(v => v.id === 'O1.1'), // Enable loyalty
    ORGANIZATION_TEST_VARIATIONS.find(v => v.id === 'O1.2'), // Disable loyalty (cleanup)
  ].filter(Boolean);

  return runOrganizationDualWriteTests(db, {
    variations: quickVariations,
    cleanup: true,
    delayMs: 5000,
    logSessionId,
  });
};

/**
 * Compare current organization state
 */
export const compareCurrentOrganization = async (options = {}) => {
  const { logSessionId } = options;
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);

  if (!organization?.id) {
    throw new Error('No organization found');
  }

  return compareOrganization(organization.id, { logSessionId });
};

export { ORGANIZATION_TEST_VARIATIONS as TEST_VARIATIONS };
